"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSelectablePlansForState = exports.getUtilitiesForState = exports.planNeedsVariant = exports.getPlanVariantIds = exports.getPlansForUtility = exports.getUtility = exports.getRatePlan = exports.CA_UTILITIES = exports.CA_RATE_PLANS = exports.URDB_SNAPSHOT = void 0;
const types_1 = require("./types");
/**
 * California utility rate plans, captured from the OpenEI Utility Rate Database.
 *
 * Committed rather than fetched. The URDB is community-maintained and its coverage is uneven —
 * of the California utilities sampled, only SCE and LADWP resolved at all, PG&E / SDG&E / SMUD
 * and the municipals returned nothing, and at least one record carried a corrupt end date (year
 * 0024). Its host domain also moved once already in 2026. None of that is a problem for a table
 * reviewed before it lands; all of it would be a problem in an install-time network call.
 *
 * Refreshing this file is a deliberate act: re-pull the utility's open-ended residential
 * schedules, diff, and commit with a new `URDB_SNAPSHOT`. The shape is stable enough that a
 * generator can emit it, which is how it was produced.
 *
 * SCE re-files rates roughly annually, so treat a snapshot more than a year old as suspect.
 */
exports.URDB_SNAPSHOT = '2026-08-10';
/**
 * Not included, and why:
 *
 * - `-CPP` (critical peak pricing) variants — pricing depends on event days the utility calls a
 *   day ahead. Nothing in the committed table can know whether yesterday was an event day.
 * - `TOU-D-A` / `TOU-D-B` (NEM 2.0) — solar customers. Net metering makes cost a function of net
 *   flow rather than consumption, which this engine does not model.
 * - `TOU-D-TEV` — separately-metered EV load, and the URDB records carry no `servicetype`, so
 *   whether they are bundled or delivery-only is unclear.
 * - `Delivery`-only twins of the TOU plans — correct only for community-choice-aggregation
 *   customers, who buy generation elsewhere. Using one for a bundled household understates the
 *   bill by roughly a third, so the all-in variant is the safer default. A CCA customer's typed-in
 *   rate still overrides everything.
 */
exports.CA_RATE_PLANS = [
    {
        id: 'sce-domestic-d',
        utilityId: 'sce',
        name: 'Domestic Service (D)',
        serviceType: 'bundled',
        tiered: true,
        variantLabel: 'Baseline Region',
        defaultVariantId: '10',
        fixedChargePerDay: 0.031,
        minChargePerDay: 0.346,
        weekdayGrid: [
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
            '111111111111111111111111',
            '111111111111111111111111',
            '111111111111111111111111',
            '111111111111111111111111',
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
        ],
        weekendGrid: [
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
            '111111111111111111111111',
            '111111111111111111111111',
            '111111111111111111111111',
            '111111111111111111111111',
            '000000000000000000000000',
            '000000000000000000000000',
            '000000000000000000000000',
        ],
        periodsByVariant: {
            // Baseline Region 5: winter 18.4 kWh/day, summer 17 kWh/day
            '5': [
                {
                    tiers: [
                        { maxKwhPerDay: 18.4, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 73.6, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 17, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 68, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 6: winter 11 kWh/day, summer 11.4 kWh/day
            '6': [
                {
                    tiers: [
                        { maxKwhPerDay: 11, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 44, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 11.4, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 45.6, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 8: winter 10.3 kWh/day, summer 12.8 kWh/day
            '8': [
                {
                    tiers: [
                        { maxKwhPerDay: 10.3, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 41.2, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 12.8, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 51.2, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 9: winter 12 kWh/day, summer 16.9 kWh/day
            '9': [
                {
                    tiers: [
                        { maxKwhPerDay: 12, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 48, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 16.9, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 67.6, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 10: winter 12.1 kWh/day, summer 19.3 kWh/day
            '10': [
                {
                    tiers: [
                        { maxKwhPerDay: 12.1, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 48.4, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 19.3, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 77.2, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 13: winter 12.2 kWh/day, summer 22.2 kWh/day
            '13': [
                {
                    tiers: [
                        { maxKwhPerDay: 12.2, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 48.8, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 22.2, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 88.8, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 14: winter 11.9 kWh/day, summer 19.2 kWh/day
            '14': [
                {
                    tiers: [
                        { maxKwhPerDay: 11.9, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 47.6, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 19.2, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 76.8, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 15: winter 9.7 kWh/day, summer 45 kWh/day
            '15': [
                {
                    tiers: [
                        { maxKwhPerDay: 9.7, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 38.8, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 45, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 180, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
            // Baseline Region 16: winter 12.4 kWh/day, summer 14.7 kWh/day
            '16': [
                {
                    tiers: [
                        { maxKwhPerDay: 12.4, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 49.6, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                },
                {
                    tiers: [
                        { maxKwhPerDay: 14.7, rate: 0.33398, adj: 0.00188 },
                        { maxKwhPerDay: 58.8, rate: 0.4299, adj: 0.00188 },
                        { maxKwhPerDay: null, rate: 0.4299, adj: 0.00188 },
                    ],
                }, // summer
            ],
        },
        urdb: {
            snapshot: exports.URDB_SNAPSHOT,
            detail: '9 records, one per baseline region: "Domestic Service: D - Baseline Region {5,6,8,9,10,13,14,15,16}", servicetype Bundled, effective 2024-06-01, open-ended',
        },
    },
    {
        id: 'sce-tou-d-4-9pm',
        utilityId: 'sce',
        name: 'TOU-D-4-9PM',
        serviceType: 'deliveryWithStandardOffer',
        tiered: false,
        defaultVariantId: types_1.DEFAULT_VARIANT,
        fixedChargePerDay: 0.031,
        minChargePerDay: 0.346,
        weekdayGrid: [
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '000000000000000011111000',
            '000000000000000011111000',
            '000000000000000011111000',
            '000000000000000011111000',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
        ],
        weekendGrid: [
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '000000000000000022222000',
            '000000000000000022222000',
            '000000000000000022222000',
            '000000000000000022222000',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
        ],
        periodsByVariant: {
            [types_1.DEFAULT_VARIANT]: [
                { tiers: [{ maxKwhPerDay: null, rate: 0.37658, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.60296, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.4889, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.40313, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.53225, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.366, adj: 0.00188 }] },
            ],
        },
        urdb: {
            snapshot: exports.URDB_SNAPSHOT,
            detail: 'URDB 66ccb7bfa7d4ef64fc07ad6b, servicetype "Delivery with Standard Offer", effective 2024-06-01, open-ended',
        },
    },
    {
        id: 'sce-tou-d-5-8pm',
        utilityId: 'sce',
        name: 'TOU-D-5-8PM',
        serviceType: 'deliveryWithStandardOffer',
        tiered: false,
        defaultVariantId: types_1.DEFAULT_VARIANT,
        fixedChargePerDay: 0.031,
        minChargePerDay: 0.346,
        weekdayGrid: [
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
            '000000000000000001110000',
            '000000000000000001110000',
            '000000000000000001110000',
            '000000000000000001110000',
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
        ],
        weekendGrid: [
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
            '000000000000000002220000',
            '000000000000000002220000',
            '000000000000000002220000',
            '000000000000000002220000',
            '333333335555555554443333',
            '333333335555555554443333',
            '333333335555555554443333',
        ],
        periodsByVariant: {
            [types_1.DEFAULT_VARIANT]: [
                { tiers: [{ maxKwhPerDay: null, rate: 0.37522, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.75403, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.56376, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.40659, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.62427, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.35591, adj: 0.00188 }] },
            ],
        },
        urdb: {
            snapshot: exports.URDB_SNAPSHOT,
            detail: 'servicetype "Delivery with Standard Offer", effective 2024-06-01, open-ended',
        },
    },
    {
        id: 'sce-tou-d-prime',
        utilityId: 'sce',
        name: 'TOU-D-PRIME',
        serviceType: 'deliveryWithStandardOffer',
        tiered: false,
        defaultVariantId: types_1.DEFAULT_VARIANT,
        fixedChargePerDay: 0.52,
        weekdayGrid: [
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '000000000000000011111000',
            '000000000000000011111000',
            '000000000000000011111000',
            '000000000000000011111000',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
        ],
        weekendGrid: [
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
            '000000000000000022222000',
            '000000000000000022222000',
            '000000000000000022222000',
            '000000000000000022222000',
            '333333335555555544444333',
            '333333335555555544444333',
            '333333335555555544444333',
        ],
        periodsByVariant: {
            [types_1.DEFAULT_VARIANT]: [
                { tiers: [{ maxKwhPerDay: null, rate: 0.26018, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.6115, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.38637, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.23957, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.57764, adj: 0.00188 }] },
                { tiers: [{ maxKwhPerDay: null, rate: 0.23957, adj: 0.00188 }] },
            ],
        },
        urdb: {
            snapshot: exports.URDB_SNAPSHOT,
            detail: 'servicetype "Delivery with Standard Offer", effective 2024-06-01, open-ended',
        },
    },
];
/**
 * Utilities whose plans are described above.
 *
 * `market: 'regulated'` is what makes zip → utility → plan resolvable at all. A deregulated
 * territory would need a different strategy, not another entry here.
 */
exports.CA_UTILITIES = [
    {
        id: 'sce',
        eiaid: 17609,
        name: 'Southern California Edison',
        state: 'CA',
        market: 'regulated',
        planIds: ['sce-domestic-d', 'sce-tou-d-4-9pm', 'sce-tou-d-5-8pm', 'sce-tou-d-prime'],
        // Tiered Domestic D is the long-standing residential schedule and still the most common,
        // though SCE now defaults new residential customers onto a TOU schedule. Neither URDB nor
        // the tariffs mark a default, so this is an assumption the installer confirms.
        defaultPlanId: 'sce-domestic-d',
    },
];
const getRatePlan = (planId) => exports.CA_RATE_PLANS.find((plan) => plan.id === planId);
exports.getRatePlan = getRatePlan;
const getUtility = (utilityId) => exports.CA_UTILITIES.find((utility) => utility.id === utilityId);
exports.getUtility = getUtility;
const getPlansForUtility = (utilityId) => exports.CA_RATE_PLANS.filter((plan) => plan.utilityId === utilityId);
exports.getPlansForUtility = getPlansForUtility;
/** Variant ids a plan prices separately, in the order they should be offered. */
const getPlanVariantIds = (plan) => {
    const ids = Object.keys(plan.periodsByVariant);
    return ids.every((id) => /^\d+$/.test(id)) ? ids.sort((a, b) => Number(a) - Number(b)) : ids;
};
exports.getPlanVariantIds = getPlanVariantIds;
/** True when a plan prices by territory and therefore needs a variant chosen. */
const planNeedsVariant = (plan) => !(types_1.DEFAULT_VARIANT in plan.periodsByVariant);
exports.planNeedsVariant = planNeedsVariant;
const STATE_ALIASES = {
    ca: 'CA',
    california: 'CA',
};
/**
 * Utilities with rate plans in a given state, for narrowing what an installer is offered.
 *
 * State rather than zip, deliberately. Utility service territories do not follow postal
 * boundaries: SCE, LADWP and a dozen municipal utilities interleave across the same Los Angeles
 * zip codes, and the sampled zip 92879 resolved to both SCE and the City of Corona. A zip table
 * would therefore be guesswork dressed as data, whereas `Hub.state` is unambiguous and already
 * populated. The installer confirms which utility, which is the one thing no lookup can settle.
 *
 * Baseline region is likewise not derivable from an address — SCE assigns regions by community,
 * and the CEC is explicit that zip codes do not correlate with climate zones. Mapping
 * `Hub.city` against SCE's published Index of Communities is the route to automating it later.
 */
const getUtilitiesForState = (state) => {
    if (!state)
        return [];
    const normalized = STATE_ALIASES[state.trim().toLowerCase()] ?? state.trim().toUpperCase();
    return exports.CA_UTILITIES.filter((utility) => utility.state === normalized);
};
exports.getUtilitiesForState = getUtilitiesForState;
/**
 * Plans an installer should be offered for a hub in a given state, most likely first.
 *
 * Empty for states with no plan tables yet — callers should fall back to the manual rate field
 * rather than presenting an empty dropdown.
 */
const getSelectablePlansForState = (state) => (0, exports.getUtilitiesForState)(state).flatMap((utility) => {
    const plans = (0, exports.getPlansForUtility)(utility.id);
    const defaultFirst = plans.filter((plan) => plan.id === utility.defaultPlanId);
    return [...defaultFirst, ...plans.filter((plan) => plan.id !== utility.defaultPlanId)];
});
exports.getSelectablePlansForState = getSelectablePlansForState;
